import { type FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useComprehension } from '../../../hooks/useComprehension';
import type { ComprehensionJobStatus } from '../../../types/api.types';

interface VideoPlayerComprehensionSectionProps {
  videoId?: string;
  episodeId?: number;
  projectId: number;
  hasTranscript: boolean;
}

interface QuestionResult {
  selected: number | null;
}

/** Maps job status values to their display string i18n keys. */
const STATUS_KEY_MAP: Partial<Record<ComprehensionJobStatus, string>> = {
  pending: 'comprehensionStatusPending',
  analyzing: 'comprehensionStatusAnalyzing',
  generating: 'comprehensionStatusGenerating',
};

export const VideoPlayerComprehensionSection: FC<VideoPlayerComprehensionSectionProps> = ({
  videoId,
  episodeId,
  projectId,
  hasTranscript,
}) => {
  const { t } = useTranslation('player');
  const { questions, pollingStatus, isPolling, isRateLimit, error, triggerGenerate } =
    useComprehension(videoId, projectId, episodeId);

  // Track selected answer index for each question (-1 = unanswered)
  const [results, setResults] = useState<QuestionResult[]>([]);

  // Reset answer state when questions are cleared (e.g., after triggerGenerate).
  // Prevents stale answers showing on re-generation when new question count equals old.
  // Guard: only call setResults when needed to avoid spurious re-renders in tests.
  useEffect(() => {
    if (questions.length === 0 && results.length > 0) {
      setResults([]);
    }
  }, [questions.length, results.length]);

  // Sync results array length to questions length when questions load
  const syncedResults: QuestionResult[] =
    questions.length > 0 && results.length !== questions.length
      ? questions.map(() => ({ selected: null }))
      : results;

  const handleAnswer = (qIdx: number, optIdx: number) => {
    if (syncedResults[qIdx]?.selected !== null) return; // already answered — lock
    const updated = syncedResults.map((r, i) =>
      i === qIdx ? { selected: optIdx } : r,
    );
    setResults(updated);
  };

  const allAnswered =
    syncedResults.length > 0 && syncedResults.every((r) => r.selected !== null);

  const score = syncedResults.filter(
    (r, i) => r.selected !== null && r.selected === questions[i]?.correctIndex,
  ).length;

  /** Computes Radio color for option at optIdx of question qIdx after answering. */
  const getOptionColor = (
    qIdx: number,
    optIdx: number,
  ): 'success' | 'error' | 'default' => {
    const result = syncedResults[qIdx];
    if (!result || result.selected === null) return 'default';
    if (optIdx === questions[qIdx].correctIndex) return 'success';
    if (optIdx === result.selected) return 'error';
    return 'default';
  };

  const getOptionSx = (qIdx: number, optIdx: number) => {
    const result = syncedResults[qIdx];
    if (!result || result.selected === null) return {};
    if (optIdx === questions[qIdx].correctIndex) return { color: 'success.main', fontWeight: 600 };
    if (optIdx === result.selected) return { color: 'error.main' };
    return { color: 'text.disabled' };
  };

  const renderContent = () => {
    // Error state — rate limit
    if (isRateLimit) {
      return (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {t('comprehensionRateLimit')}
        </Alert>
      );
    }

    // Error state — other
    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 1 }}>
          {t('comprehensionError')}
        </Alert>
      );
    }

    // Polling state
    if (isPolling) {
      const statusKey = pollingStatus ? STATUS_KEY_MAP[pollingStatus] : undefined;
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            {statusKey ? t(statusKey) : t('comprehensionStatusPending')}
          </Typography>
        </Stack>
      );
    }

    // Loaded state
    if (questions.length > 0) {
      return (
        <Stack spacing={2}>
          {questions.map((q, qIdx) => {
            const result = syncedResults[qIdx] ?? { selected: null };
            return (
              <Box key={q.id} data-testid={`comprehension-question-${qIdx}`}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  {q.questionText}
                </Typography>
                <FormControl component="fieldset" disabled={result.selected !== null}>
                  <RadioGroup
                    value={result.selected !== null ? String(result.selected) : ''}
                    onChange={(e) => handleAnswer(qIdx, Number(e.target.value))}
                  >
                    {q.options.map((option, optIdx) => (
                      <FormControlLabel
                        key={optIdx}
                        value={String(optIdx)}
                        control={
                          <Radio
                            size="small"
                            color={getOptionColor(qIdx, optIdx) === 'default' ? 'primary' : getOptionColor(qIdx, optIdx)}
                          />
                        }
                        label={
                          <Typography variant="body2" sx={getOptionSx(qIdx, optIdx)}>
                            {option}
                          </Typography>
                        }
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Box>
            );
          })}
        </Stack>
      );
    }

    // Idle state — no questions yet
    return (
      <Stack spacing={1.5} alignItems="flex-start">
        <Typography variant="body2" color="text.secondary">
          {hasTranscript
            ? t('comprehensionEmptyState')
            : t('comprehensionNoTranscript')}
        </Typography>
        {hasTranscript && (
          <Button
            variant="outlined"
            size="small"
            onClick={triggerGenerate}
            data-testid="comprehension-get-questions-button"
          >
            {t('comprehensionGetQuestionsButton')}
          </Button>
        )}
      </Stack>
    );
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        borderTop: 1,
        borderColor: 'divider',
        flexShrink: 0,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="comprehension-panel-content"
        id="comprehension-panel-header"
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t('comprehensionSectionTitle')}
        </Typography>
        {allAnswered && (
          <Chip
            size="small"
            label={t('comprehensionScore', { score, total: questions.length })}
            color={score === questions.length ? 'success' : 'default'}
            sx={{ ml: 1, height: 18, fontSize: '0.7rem' }}
          />
        )}
      </AccordionSummary>
      <AccordionDetails
        id="comprehension-panel-content"
        aria-labelledby="comprehension-panel-header"
        sx={{ p: 0 }}
      >
        <Box sx={{ px: 2, pb: 2 }}>{renderContent()}</Box>
      </AccordionDetails>
    </Accordion>
  );
};
